// Staff species view - species management

import { css, html } from 'lit';
import { api } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import { router } from '../../lib/router.js';

// Pencil icon SVG
const pencilIcon = html`<svg
  xmlns="http://www.w3.org/2000/svg"
  width="16"
  height="16"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="2"
  stroke-linecap="round"
  stroke-linejoin="round"
>
  <path
    d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z"
  />
</svg>`;

// Trash icon SVG
const trashIcon = html`<svg
  xmlns="http://www.w3.org/2000/svg"
  width="16"
  height="16"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="2"
  stroke-linecap="round"
  stroke-linejoin="round"
>
  <path d="M3 6h18" />
  <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
  <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
</svg>`;

// Clipboard icon SVG
const clipboardIcon = html`<svg
  xmlns="http://www.w3.org/2000/svg"
  width="14"
  height="14"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="2"
  stroke-linecap="round"
  stroke-linejoin="round"
>
  <rect width="8" height="4" x="8" y="2" rx="1" ry="1" />
  <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
</svg>`;

/**
 * Staff species view component for managing species
 */
class StaffSpeciesView extends BaseComponent {
  static properties = {
    _speciesList: { type: Array, state: true },
    _loading: { type: Boolean, state: true },
    _error: { type: String, state: true },
    _showDeleteModal: { type: Boolean, state: true },
    _speciesToDelete: { type: String, state: true },
  };

  static styles = css`
    :host {
      display: block;
    }
  `;

  constructor() {
    super();
    /** @type {Array<import('../../types/jsdoc-types.js').Species>} */
    this._speciesList = [];
    this._loading = true;
    this._error = null;
    this._showDeleteModal = false;
    this._speciesToDelete = null;
  }

  async connectedCallback() {
    await super.connectedCallback();
    this._loadSpecies();
  }

  async _loadSpecies() {
    this._loading = true;
    this._error = null;

    try {
      this._speciesList = await api.get('/api/staff/species');
    } catch (err) {
      console.error('Failed to load species:', err);
      this._error = /** @type {Error} */ (err).message || 'Failed to load species';
    } finally {
      this._loading = false;
    }
  }

  _goToNewSpecies() {
    router.navigate('/staff/species/new');
  }

  /** @param {string} slug */
  _editSpecies(slug) {
    router.navigate(`/staff/species/${slug}/edit`);
  }

  /** @param {string} slug */
  _deleteSpecies(slug) {
    this._speciesToDelete = slug;
    this._showDeleteModal = true;
  }

  async _confirmDelete() {
    if (!this._speciesToDelete) return;

    try {
      await api.delete(`/api/staff/species/${this._speciesToDelete}`);
      this._speciesToDelete = null;
      this._showDeleteModal = false;
      await this._loadSpecies();
    } catch (err) {
      console.error('Failed to delete species:', err);
      this._error = /** @type {Error} */ (err).message || 'Failed to delete species';
    }
  }

  _cancelDelete() {
    this._showDeleteModal = false;
    this._speciesToDelete = null;
  }

  /**
   * @param {string} description
   * @param {number} [maxLength]
   */
  _truncateDescription(description, maxLength = 100) {
    if (!description) return '';
    if (description.length <= maxLength) return description;
    return `${description.substring(0, maxLength)}...`;
  }

  /** @param {import('../../types/jsdoc-types.js').Species} species */
  _getSizeDisplay(species) {
    const sizes = species.speciesJson?.size || [];
    if (sizes.length === 0) return 'Medium';
    return sizes.map((/** @type {string} */ s) => s.charAt(0).toUpperCase() + s.slice(1)).join(' or ');
  }

  /** @param {import('../../types/jsdoc-types.js').Species} species */
  _getSpeedDisplay(species) {
    return species.speciesJson?.speed || 30;
  }

  /**
   * @param {string} text
   * @param {Event} event
   */
  _copyToClipboard(text, event) {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        const btn = /** @type {HTMLElement} */ (event.target).closest('button');
        if (btn) {
          btn.classList.add('btn-success');
          btn.classList.remove('btn-outline-secondary');
          setTimeout(() => {
            btn.classList.remove('btn-success');
            btn.classList.add('btn-outline-secondary');
          }, 2000);
        }
      })
      .catch((err) => {
        console.error('Failed to copy:', err);
      });
  }

  render() {
    return html`
      <nav aria-label="breadcrumb">
        <ol class="breadcrumb">
          <li class="breadcrumb-item">Staff</li>
          <li class="breadcrumb-item active" aria-current="page">Species</li>
        </ol>
      </nav>

      ${this._error ? html` <div class="alert alert-danger">${this._error}</div> ` : ''}

      <div class="d-flex justify-content-between align-items-center mb-3">
        <h2>Species</h2>
        <button class="btn btn-primary" @click="${this._goToNewSpecies}">Create Species</button>
      </div>

      ${
        this._loading
          ? html`
            <div class="text-center py-4">
              <div class="spinner-border">
                <span class="visually-hidden">Loading...</span>
              </div>
            </div>
          `
          : this._speciesList.length === 0
            ? html` <p class="text-muted">No species yet.</p> `
            : html`
              <div class="d-flex flex-wrap gap-3">
                ${this._speciesList.map(
                  (species) => html`
                    <div style="flex: 1 1 280px; min-width: 260px; max-width: 320px;">
                      <div class="card h-100">
                        <div class="card-body">
                          <div class="d-flex justify-content-between align-items-start mb-2">
                            <h6 class="card-title mb-0">${species.name}</h6>
                            <div class="d-flex gap-1">
                              <button
                                class="btn btn-sm btn-outline-primary"
                                title="Edit"
                                @click="${() => this._editSpecies(species.slug)}"
                              >
                                ${pencilIcon}
                              </button>
                              <button
                                class="btn btn-sm btn-outline-danger"
                                title="Delete"
                                @click="${() => this._deleteSpecies(species.slug)}"
                              >
                                ${trashIcon}
                              </button>
                            </div>
                          </div>

                          <div class="d-flex align-items-center gap-2 mb-2">
                            <code class="small text-muted">${species.slug}</code>
                            <button
                              class="btn btn-sm btn-outline-secondary py-0 px-2"
                              title="Copy Slug"
                              @click="${(/** @type {Event} */ e) => this._copyToClipboard(species.slug, e)}"
                            >
                              ${clipboardIcon}
                            </button>
                          </div>

                          <div class="mb-2">
                            <div class="d-flex gap-1 mb-2">
                              <span class="badge bg-secondary">v${species.version || 1}</span>
                              ${
                                species.published
                                  ? html` <span class="badge bg-success">Published</span> `
                                  : html` <span class="badge bg-warning text-dark">Draft</span> `
                              }
                              <span class="badge bg-secondary">${species.creatureType}</span>
                            </div>
                          </div>

                          ${
                            species.description
                              ? html`
                                <p class="small text-muted mb-2">${this._truncateDescription(species.description)}</p>
                              `
                              : ''
                          }

                          <div class="small">
                            <div><strong>Size:</strong> ${this._getSizeDisplay(species)}</div>
                            <div><strong>Speed:</strong> ${this._getSpeedDisplay(species)} ft.</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  `,
                )}
              </div>
            `
      }

      <!-- Delete Confirmation Modal -->
      ${
        this._showDeleteModal
          ? html`
            <div class="modal show d-block" style="background: rgba(0,0,0,0.5);">
              <div class="modal-dialog">
                <div class="modal-content">
                  <div class="modal-header">
                    <h5 class="modal-title">Delete Species</h5>
                    <button type="button" class="btn-close" @click="${this._cancelDelete}"></button>
                  </div>
                  <div class="modal-body">
                    <p>Are you sure you want to delete this species? This cannot be undone.</p>
                  </div>
                  <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" @click="${this._cancelDelete}">Cancel</button>
                    <button type="button" class="btn btn-danger" @click="${this._confirmDelete}">Delete</button>
                  </div>
                </div>
              </div>
            </div>
          `
          : ''
      }
    `;
  }
}

customElements.define('staff-species-view', StaffSpeciesView);
